import { useState } from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IconBookmark,
  IconDots,
  IconGripVertical,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import ShareV2Icon from "../../../assets/icons/share-v2.svg?react";
import { displayToast } from "../../../components/app/AppToast";
import { DesktopConfirmDialog } from "../../../components/app/DesktopConfirmDialog";
import { useConfirmation } from "../../../hooks/useConfirmation";
import { openDesktopSearchPalette } from "../../../components/search/DesktopSearchPaletteHost";
import {
  DesktopMenu,
  DesktopMenuItem,
} from "../../../components/ui/DesktopMenu";
import { usePopoverMenu } from "../../../components/ui/Popover";
import { SectionLink } from "../../../components/ui/SectionLink";
import { FLAG_PLAYLIST_SUMMARY } from "../../../config/featureFlags";
import { journalImpactFactor } from "../../../domains/journal/utils/journalImpactFactor";
import { useConsumeLocationState } from "../../../domains/app/hooks/useLocationState";
import { reloadCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import { cn } from "../../../lib/utils";
import {
  apiClient,
  useApiQuery,
  useApiRevalidate,
} from "../../../services/api";
import { PlaylistDescription } from "../PlaylistPageHeader";

/** How many saved papers the empty collection offers to add. */
const SAVED_SUGGESTION_COUNT = 4;

/** The collection's own reads, which every edit refreshes. */
export function useRevalidateOwnCollection(
  playlistId: string
): () => Promise<void> {
  const revalidate = useApiRevalidate();
  return async () => {
    await Promise.all([
      revalidate("/webapp/public/playlists/:playlistId", {
        param: { playlistId },
      }),
      revalidate("/webapp/private/playlists/me"),
      // Saved holds every collection's members: a removal or an Undo can
      // change what the bookmarks show.
      revalidate("/webapp/private/saved"),
      revalidate("/webapp/private/feed/saved"),
    ]);
  };
}

type Editing = "none" | "title" | "description";

type OwnCollectionFields = {
  id: string;
  title: string;
  description: string | null;
  descriptionLabel: string | null;
};

/**
 * The owner's edits of a collection: which field is in edit, renaming,
 * describing, and the delete confirmation. The header only draws them.
 */
function useOwnCollectionEditor(
  playlist: OwnCollectionFields,
  startsRenaming: boolean
) {
  const { t } = useTranslation();
  const history = useHistory();
  const revalidateCollection = useRevalidateOwnCollection(playlist.id);
  const revalidate = useApiRevalidate();
  const [editing, setEditing] = useState<Editing>(
    startsRenaming ? "title" : "none"
  );
  const deletion = useConfirmation<null>();

  async function rename(title: string): Promise<void> {
    setEditing("none");
    if (title === "" || title === playlist.title) return;
    try {
      await apiClient.post("/webapp/private/playlists/:playlistId/rename", {
        param: { playlistId: playlist.id },
        json: { title },
      });
      await revalidateCollection();
      await reloadCurrentUser();
      displayToast(t("Collection renamed"), "success");
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  async function describe(description: string): Promise<void> {
    setEditing("none");
    const current = playlist.description === null ? "" : playlist.description;
    if (description === current) return;
    try {
      await apiClient.patch("/webapp/private/playlists/:playlistId/summary", {
        param: { playlistId: playlist.id },
        json: {
          description: description === "" ? null : description,
          descriptionLabel:
            description === "" ? null : playlist.descriptionLabel,
        },
      });
      await revalidateCollection();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  async function deleteCollection(): Promise<"done" | "failed"> {
    try {
      await apiClient.delete("/webapp/private/playlists/:playlistId", {
        param: { playlistId: playlist.id },
      });
      await Promise.all([
        revalidate("/webapp/private/playlists/me"),
        revalidate("/webapp/private/saved"),
        revalidate("/webapp/private/feed/saved"),
      ]);
      await reloadCurrentUser();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return "failed";
    }
    history.replace("/profile/playlist");
    displayToast(t("Collection deleted"), "success");
    return "done";
  }

  return {
    editing,
    startEditing: (field: "title" | "description") => {
      setEditing(field);
    },
    rename,
    describe,
    deletion: deletion.state.status,
    openDelete: () => {
      deletion.ask(null);
    },
    closeDelete: deletion.cancel,
    confirmDelete: () => {
      void deletion.confirm(deleteCollection);
    },
  };
}

/**
 * The header of a collection its owner opens (Figma "03 - My Collection /
 * Collection Page", "D3.library.collection"): the title renames on a click,
 * the description edits in place, and the ··· menu renames, edits the
 * description or deletes. Changing the background is set aside (Figma
 * annotation), as are Listen and Ask JuisciGPT.
 */
export function OwnCollectionHeader({
  playlist,
  articleCount,
  startsRenaming,
  curator,
  facts,
  onShare,
}: {
  playlist: {
    id: string;
    title: string;
    description: string | null;
    descriptionLabel: string | null;
    summary: string | null;
    metrics: { shares: number };
  };
  articleCount: number;
  /** A collection just created opens with its title in edit. */
  startsRenaming: boolean;
  curator: React.JSX.Element;
  facts: React.JSX.Element;
  onShare: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const menu = usePopoverMenu("right");
  const consumeLocationState = useConsumeLocationState();
  const {
    editing,
    startEditing,
    rename,
    describe,
    deletion,
    openDelete,
    closeDelete,
    confirmDelete,
  } = useOwnCollectionEditor(playlist, startsRenaming);

  const description = playlist.description;
  const summary = playlist.summary;
  return (
    <>
      <p className="m-0 text-footnote text-ds-slate-600">
        {t("Your collection")}
      </p>
      {editing === "title" ? (
        <div className="flex flex-col gap-1">
          <input
            autoFocus
            maxLength={200}
            defaultValue={playlist.title}
            aria-label={t("Collection name")}
            onFocus={(event) => {
              event.currentTarget.select();
              // A command of the creation, not page state: a Back or a
              // reload must not open the title in edit again.
              if (startsRenaming) consumeLocationState();
            }}
            onBlur={(event) => {
              void rename(event.currentTarget.value.trim());
            }}
            onKeyDown={(event) => {
              // Leaving the field saves it; Escape restores the title first,
              // so the save that follows changes nothing.
              if (event.key === "Escape")
                event.currentTarget.value = playlist.title;
              if (event.key === "Escape" || event.key === "Enter")
                event.currentTarget.blur();
            }}
            className="m-0 w-full border-0 border-b-2 border-solid border-ds-mint-700 bg-transparent p-0 font-dm-sans text-desktop-title font-semibold text-ds-black outline-none selection:bg-ds-mint-100"
          />
          <span className="text-caption text-ds-slate-600">
            {t("Press ↵ to save")}
          </span>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            startEditing("title");
          }}
          className="group flex cursor-text items-center gap-2.5 border-none bg-transparent p-0 text-left"
        >
          <h1 className="m-0 text-desktop-title font-semibold text-ds-black">
            {playlist.title}
          </h1>
          <IconPencil
            aria-hidden
            className="size-5 shrink-0 text-ds-slate-600 opacity-0 group-hover:opacity-100"
            stroke={1.5}
          />
        </button>
      )}
      {/* Without the description editor (FLAG_PLAYLIST_SUMMARY), the owner
          reads what every reader reads: the description, else the summary. */}
      {!FLAG_PLAYLIST_SUMMARY ? (
        description !== null && description !== "" ? (
          <div className="text-desktop-body text-ds-slate-700 [&_p]:m-0">
            <PlaylistDescription
              description={description}
              label={playlist.descriptionLabel}
            />
          </div>
        ) : summary !== null && summary !== "" ? (
          <p className="m-0 text-desktop-body whitespace-pre-line text-ds-slate-700">
            {summary}
          </p>
        ) : null
      ) : editing === "description" ? (
        <textarea
          autoFocus
          rows={2}
          defaultValue={description === null ? "" : description}
          placeholder={t("Add a description")}
          aria-label={t("Description")}
          onBlur={(event) => {
            void describe(event.currentTarget.value.trim());
          }}
          onKeyDown={(event) => {
            // As for the title: Escape restores, then leaving saves nothing.
            if (event.key === "Escape") {
              event.currentTarget.value =
                description === null ? "" : description;
              event.currentTarget.blur();
            }
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              event.currentTarget.blur();
            }
          }}
          className="w-full resize-none rounded-lg border border-solid border-ds-slate-200 bg-ds-white px-3 py-2 font-dm-sans text-desktop-body text-ds-slate-700 outline-none focus:border-ds-slate-400"
        />
      ) : (description === null || description === "") &&
        summary !== null &&
        summary !== "" ? (
        // The summary stands in for an empty description, as for every
        // reader; the ··· menu adds a description over it.
        <p className="m-0 text-desktop-body whitespace-pre-line text-ds-slate-700">
          {summary}
        </p>
      ) : description === null || description === "" ? (
        <button
          type="button"
          onClick={() => {
            startEditing("description");
          }}
          className="cursor-text self-start border-none bg-transparent p-0 text-left text-desktop-body text-ds-slate-500 hover:text-ds-slate-700"
        >
          {t("Add a description")}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => {
            startEditing("description");
          }}
          className="cursor-text border-none bg-transparent p-0 text-left text-desktop-body text-ds-slate-700 [&_p]:m-0"
        >
          <PlaylistDescription
            description={description}
            label={playlist.descriptionLabel}
          />
        </button>
      )}
      {curator}
      {facts}
      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={onShare}
          className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-ds-slate-100 bg-ds-white py-1.5 pr-2.5 pl-3 text-footnote font-semibold text-ds-black hover:bg-ds-slate-100"
        >
          <ShareV2Icon className="size-4 shrink-0" />
          {t("Share")}
        </button>
        <button
          ref={menu.anchor.triggerRef}
          type="button"
          aria-label={t("More")}
          aria-haspopup="menu"
          aria-expanded={menu.isOpen}
          onClick={menu.toggle}
          className={cn(
            "flex size-8.5 cursor-pointer items-center justify-center rounded-lg border border-ds-slate-100 bg-ds-white p-0 text-ds-black hover:bg-ds-slate-100",
            menu.isOpen ? "bg-ds-slate-100" : null
          )}
        >
          <IconDots className="size-5" stroke={1.5} />
        </button>
        {menu.isOpen ? (
          <DesktopMenu anchor={menu.anchor} className="w-65">
            <DesktopMenuItem
              description={null}
              icon={IconPencil}
              label={t("Rename")}
              trailing={null}
              tone="default"
              action={{
                kind: "button",
                onClick: () => {
                  menu.close();
                  startEditing("title");
                },
              }}
            />
            {FLAG_PLAYLIST_SUMMARY ? (
              <DesktopMenuItem
                description={null}
                icon={IconPencil}
                label={t("Edit description")}
                trailing={null}
                tone="default"
                action={{
                  kind: "button",
                  onClick: () => {
                    menu.close();
                    startEditing("description");
                  },
                }}
              />
            ) : null}
            <DesktopMenuItem
              description={null}
              icon={IconTrash}
              label={t("Delete collection")}
              trailing={null}
              tone="danger"
              action={{
                kind: "button",
                onClick: () => {
                  menu.close();
                  openDelete();
                },
              }}
            />
          </DesktopMenu>
        ) : null}
      </div>
      {deletion === "closed" ? null : (
        <DesktopConfirmDialog
          title={t("Delete “{{title}}”?", { title: playlist.title })}
          confirmLabel={t("Delete")}
          note={t("This can't be undone.")}
          disabled={deletion === "running"}
          confirmDisabled={false}
          onConfirm={confirmDelete}
          onCancel={closeDelete}
        >
          <p className="m-0 font-dm-sans text-desktop-body-s text-ds-slate-700">
            {[
              t(
                "The {{count}} articles stay in Saved and in your other collections.",
                {
                  count: articleCount,
                }
              ),
              // Only a link that went out can stop working.
              ...(playlist.metrics.shares === 0
                ? []
                : [t("The link you shared will stop working.")]),
              t("This can't be undone."),
            ].join(" ")}
          </p>
        </DesktopConfirmDialog>
      )}
    </>
  );
}

/**
 * An empty collection (Figma "D3.library.collection.new"): how to fill it,
 * a search, then the reader's latest saved papers, each with Add.
 */
export function OwnCollectionEmpty({
  playlistId,
}: {
  playlistId: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const revalidateCollection = useRevalidateOwnCollection(playlistId);
  const savedQuery = useApiQuery("/webapp/private/feed/saved", {
    query: { limit: SAVED_SUGGESTION_COUNT, offset: 0 },
  });

  async function add(slug: string): Promise<void> {
    try {
      await apiClient.post("/webapp/private/playlists/:playlistId/article", {
        param: { playlistId },
        json: { slug, position: null },
      });
      await revalidateCollection();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-ds-slate-200 p-8  text-center">
        <span className="flex size-10 items-center justify-center rounded-full bg-ds-mint-50 text-ds-mint-800">
          <IconBookmark className="size-5" stroke={1.5} />
        </span>
        <p className="m-0 text-heading-xs font-semibold text-ds-black">
          {t("This collection is empty")}
        </p>
        <p className="m-0 max-w-gpt-context-panel text-desktop-body-s text-ds-slate-600">
          {t(
            "Search for articles below, or click the bookmark on any article and choose this collection."
          )}
        </p>
        <button
          type="button"
          onClick={(event) => {
            openDesktopSearchPalette(event.currentTarget);
          }}
          className="mt-3 flex h-10 w-full max-w-87 cursor-text items-center gap-2 rounded-xl border border-solid border-ds-slate-200 bg-ds-white px-4 text-footnote text-ds-slate-500 hover:border-ds-slate-400"
        >
          <IconSearch className="size-4 shrink-0" stroke={1.5} />
          {t("Search articles, journals or authors")}
        </button>
      </div>
      {savedQuery.state !== "success" ||
      savedQuery.body.docs.length === 0 ? null : (
        <section className="flex flex-col">
          <div className="flex items-center justify-between pb-2">
            <h2 className="m-0 text-caption font-semibold tracking-wide text-ds-slate-600 uppercase">
              {t("From your saves")}
            </h2>
            <SectionLink to="/discovery?filter=saved">
              {t("View all {{count}} in Saved →", {
                count: savedQuery.body.meta.total,
              })}
            </SectionLink>
          </div>
          <div className="flex flex-col divide-y divide-ds-slate-100 border-t border-ds-slate-100">
            {savedQuery.body.docs.map((article) => {
              const journal = article.journal;
              const impactFactor =
                journal === null ? null : journalImpactFactor(journal);
              return (
                <div key={article.id} className="flex items-center gap-4 py-3">
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <p className="m-0 text-footnote font-semibold text-ds-black">
                      {article.title}
                    </p>
                    {journal === null ? null : (
                      <p className="m-0 text-caption text-ds-slate-600">
                        {impactFactor === null
                          ? journal.name
                          : `${journal.name} · ${t("IF {{value}}", { value: impactFactor.toFixed(1) })}`}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      void add(article.slug);
                    }}
                    className="flex shrink-0 cursor-pointer items-center gap-1 rounded-lg border border-ds-slate-100 bg-ds-white py-1.5 pr-3 pl-2.5 text-footnote font-semibold text-ds-black hover:bg-ds-slate-100"
                  >
                    <IconPlus className="size-4" stroke={1.5} />
                    {t("Add")}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

/** One article of the owner's list, with its drag handle on hover. */
function SortableArticle({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      className={cn(
        "group/sortable relative bg-ds-white",
        // The list's divider would draw across the lifted row.
        isDragging
          ? "z-10 rounded-sm border-transparent shadow-desktop-drag"
          : null
      )}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        aria-label={t("Drag to reorder")}
        {...attributes}
        {...listeners}
        className={cn(
          "absolute top-4 -left-8 flex size-6 cursor-grab items-center justify-center rounded-md border-none bg-transparent p-0 text-ds-slate-500 opacity-0 hover:bg-ds-slate-100 focus-visible:opacity-100 active:cursor-grabbing",
          // Outside the lifted row's margin, the handle would float alone.
          isDragging ? null : "group-hover/sortable:opacity-100"
        )}
      >
        <IconGripVertical className="size-4" stroke={1.5} />
      </button>
      {children}
    </div>
  );
}

// The list only reorders: a dragged row stays in its column.
const verticalOnly: Modifier = ({ transform }) => ({ ...transform, x: 0 });

/**
 * The owner's articles, which a drag reorders (Figma "ArticleRow · survol
 * (glisser · écouter · retirer)"). The new order shows at once and is saved
 * as the whole list; the collection's ids, not the rows, are reordered, so an
 * article the list does not draw keeps its place in the order.
 */
export function OwnCollectionSortableArticles<Article extends { id: string }>({
  playlistId,
  orderedIds,
  articles,
  renderArticle,
}: {
  playlistId: string;
  /** The collection's article ids, in its stored order. */
  orderedIds: ReadonlyArray<string>;
  articles: ReadonlyArray<Article>;
  renderArticle: (article: Article) => React.JSX.Element;
}): React.JSX.Element {
  const { t } = useTranslation();
  const revalidateCollection = useRevalidateOwnCollection(playlistId);
  // The order just dropped, shown until the collection reloads with it.
  const [pendingOrder, setPendingOrder] =
    useState<ReadonlyArray<string> | null>(null);
  const sensors = useSensors(
    // A click on the handle is not a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const order = pendingOrder === null ? orderedIds : pendingOrder;
  // An older collection can hold an article twice: its first copy places it,
  // as the drag below moves the first copy.
  const rank = new Map<string, number>();
  for (const [index, id] of order.entries()) {
    if (!rank.has(id)) rank.set(id, index);
  }
  // While the collection reloads, the list still holds the previous rows: a
  // removed article is dropped from them, not drawn.
  const sorted = articles
    .filter((article) => rank.has(article.id))
    .sort((a, b) => {
      const ra = rank.get(a.id);
      const rb = rank.get(b.id);
      if (ra === undefined || rb === undefined)
        throw new Error(
          "should not happen: a drawn article is in the collection"
        );
      return ra - rb;
    });

  async function save(next: ReadonlyArray<string>): Promise<void> {
    setPendingOrder(next);
    try {
      await apiClient.post(
        "/webapp/private/playlists/:playlistId/articles/order",
        { param: { playlistId }, json: { articleIds: [...next] } }
      );
      await revalidateCollection();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      // A conflict means the list changed elsewhere: reload it, so the next
      // drag sends the current articles.
      await revalidateCollection();
    } finally {
      setPendingOrder(null);
    }
  }

  function onDragEnd(event: DragEndEvent): void {
    const over = event.over;
    if (over === null || over.id === event.active.id) return;
    const from = order.indexOf(String(event.active.id));
    const to = order.indexOf(String(over.id));
    if (from === -1 || to === -1)
      throw new Error(
        "should not happen: a dragged article is in the collection"
      );
    void save(arrayMove([...order], from, to));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[verticalOnly]}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={sorted.map((article) => article.id)}
        strategy={verticalListSortingStrategy}
        // One save at a time: a second drag sent before the first lands
        // could be overwritten by it.
        disabled={pendingOrder !== null}
      >
        {sorted.map((article) => (
          <SortableArticle key={article.id} id={article.id}>
            {renderArticle(article)}
          </SortableArticle>
        ))}
      </SortableContext>
    </DndContext>
  );
}
